import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
//import { Search } from "lucide-react";
import { Blog } from "@/types";
import { Button } from "./ui/button";
//import { useAuth } from "../hooks/useAuth";
import { BlogRepository } from "@/infrastructure/repositories/BlogRepository";

interface BlogPageProps {
  onNavigate: (page: string) => void;
}

const PAGE_SIZE = 8;

export function BlogPage({ onNavigate }: BlogPageProps) {
  const blogRepo = new BlogRepository();
  const { theme } = useTheme();
 // const { clearError } = useAuth();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  console.log(loading);
  const [searchQuery, setSearchQuery] = useState("");
  console.log(setSearchQuery)
  const navigate = (url: string, page: string, onNavigate: (p: string) => void) => {
      window.history.pushState({}, "", url);
      onNavigate(page);
    };

  // ----------------------------------
  // BLOG MAPPER
  // ----------------------------------
  const mapApiBlogToBlog = (apiBlog: any): Blog => ({
  id: Number(apiBlog.post_id),
  slug: apiBlog.post_slug ?? "",
  title: apiBlog.post_title ?? "",
  excerpt: apiBlog.post_meta_desc ?? "",
  content: apiBlog.post_desc ?? "",

  category: String(apiBlog.post_category ?? ""),
  featured: String(apiBlog.post_category) === "19",

  author: apiBlog.author ?? "Admin",
  date: apiBlog.post_created_at
    ? new Date(Number(apiBlog.post_created_at) * 1000).toLocaleDateString()
    : "",
  readTime: apiBlog.read_time ? String(apiBlog.read_time) : "5 min read",

  image: apiBlog.post_img
    ? `https://api.swimpractice.com/${apiBlog.post_img}`
    : "",

  authorImage: apiBlog.author_image ?? undefined,
});


  // ----------------------------------
  // INITIAL LOAD (PAGE 1)
  // ----------------------------------
  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);

      const apiBlogs = await blogRepo.getBlogList(1, PAGE_SIZE);
      const mapped = apiBlogs.map(mapApiBlogToBlog);

      setBlogs(mapped);
      setPage(2);
      setHasMore(apiBlogs.length === PAGE_SIZE);

      setLoading(false);
    };

    loadInitial();
  }, []);

  // ----------------------------------
  // LOAD MORE (PAGE BASED)
  // ----------------------------------
  const loadMoreBlogs = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);

    const apiBlogs = await blogRepo.getBlogList(page, PAGE_SIZE);
    const mapped = apiBlogs.map(mapApiBlogToBlog);

    setBlogs(prev => [...prev, ...mapped]);
    setPage(prev => prev + 1);

    if (apiBlogs.length < PAGE_SIZE) {
      setHasMore(false);
    }

    setLoadingMore(false);
  };

  // ----------------------------------
  // INTERSECTION OBSERVER
  // ----------------------------------
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMoreBlogs();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [page, hasMore, loadingMore]);

  // ----------------------------------
  // SEARCH + FILTER
  // ----------------------------------
  const filteredBlogs = blogs.filter(blog => {
    const q = searchQuery.toLowerCase();
    return (
      blog.title.toLowerCase().includes(q) ||
      blog.excerpt.toLowerCase().includes(q) ||
      blog.category?.toLowerCase().includes(q) ||
      blog.author.toLowerCase().includes(q)
    );
  });

  const featuredBlogs = filteredBlogs.filter(b => b.featured);
  const regularBlogs = filteredBlogs.filter(b => !b.featured);

  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="border-b glass-header sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between">
          <img
            src={logoSrc}
            alt="Swimnerd Logo"
            className="h-12 cursor-pointer"
            onClick={() => navigate("/", "landing", onNavigate)}
          />

           <div className="flex gap-4">
            <Button variant="ghost" onClick={() => navigate("/find-pool", "find-pool", onNavigate)}>
              Find Pool
            </Button>
            <Button variant="ghost" onClick={() => navigate("/blog", "blog", onNavigate)}>
              Blog
            </Button>
            <Button variant="ghost" onClick={() => navigate("/signin", "signin", onNavigate)}>
              Sign In
            </Button>
            <Button onClick={() => navigate("/signup", "signup", onNavigate)}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(var(--primary)/0.85))]" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          {/* <h1 className="text-sm uppercase tracking-wider text-white/80 mb-4">
            Blog
          </h1> */}

          <h2 className="text-5xl font-bold mb-6 max-w-3xl leading-tight">
            Insights to Swim Smarter, Faster, and Stronger
          </h2>

          <p className="text-lg text-white/90 max-w-2xl">
            Expert coaching tips, training science, and AI-powered insights to help swimmers
            and coaches make better decisions in and out of the pool.
          </p>
        </div>
      </section>


      {/* Search */}
      <div className="px-6 mt-6">
        {/* <div className="relative max-w-3xl mx-auto">
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search blogs..."
            className="w-full border rounded-xl px-4 py-3 pr-10"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div> */}
      </div>

      {/* Blogs */}
      <main className="flex-1 max-w-8xl mx-auto px-6 pt-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Blog List */}
          <div className="lg:col-span-9">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {regularBlogs.map(blog => (
                <article
                  key={blog.id}
                 onClick={() => {
                  const url = `/blog/${blog.id}/${blog.slug}`;
                  window.history.pushState({}, "", url);
                  onNavigate(`blog-${blog.id}-${blog.slug}`);
                }}
                  className="bg-white rounded-2xl border hover:shadow-xl transition cursor-pointer"
                >
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="h-52 w-full object-cover rounded-t-2xl"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold mb-2 line-clamp-2">
                      {blog.title}
                    </h3>
                    
                  </div>
                </article>
              ))}
            </div>

            {loadingMore && (
              // <div className="text-center text-gray-500 mt-6">
              //   Loading more blogs...
              // </div>

              <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              </div>
            )}

            {hasMore && <div ref={loadMoreRef} className="h-10" />}
          </div>

          {/* Featured */}
          {featuredBlogs.length > 0 && (
            <aside className="lg:col-span-3 sticky top-28">
              <h3 className="text-xl font-semibold mb-6">
                Featured Blogs
              </h3>
              <div className="space-y-4">
                {featuredBlogs.map(blog => (
                  <div
                    key={blog.id}
                    onClick={() => {
                        const url = `/blog/${blog.id}/${blog.slug}`;
                        window.history.pushState({}, "", url);
                        onNavigate(`blog-${blog.id}-${blog.slug}`);
                      }}
                    className="bg-white border rounded-xl cursor-pointer hover:shadow-lg"
                  >
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="h-32 w-full object-cover rounded-t-xl"
                    />
                    <div className="p-4">
                      <h4 className="text-sm font-semibold line-clamp-2">
                        {blog.title}
                      </h4>
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          )}
        </div>
      </main>

      <footer className="border-t bg-white text-center py-10 text-sm text-gray-500">
        © {new Date().getFullYear()} Swimnerd. Built for smarter swimming.
      </footer>
    </div>
  );
}

